import React, { ComponentType, MutableRefObject, useEffect, useMemo, useState } from 'react';

import { VisuallyHidden } from '@rbilabs/components-library/build/components/visually-hidden';
import { useIdCounter } from '@rbilabs/components-library/build/hooks/use-id-counter';
import { FieldProps } from 'formik';
import MaskedInput from 'react-text-mask';

import IconCheck from 'components/icons/check';
import { FormValidationState } from 'utils/form';

import { FloatingLabelInputWrapper, IconContainer, Label, Message, StyledInput } from './styled';
import { IFloatingLabelInputProps } from './types';
import useInputHandles from './use-input-handles';
import useTestIds from './use-test-ids';

const isMobile = /Mobile/.test(navigator.userAgent);

/** @deprecated Use TextInput from '@rbilabs/components-library' instead */
export const FloatingLabelInput = React.forwardRef(
  (
    {
      'data-testid': testId,
      onBlur,
      onFocus,
      label,
      value,
      validation,
      maxLength,
      enableDefaultAlert,
      message,
      onChange,
      className,
      autoComplete,
      pattern,
      hasFooter,
      footer,
      mask,
      labelId,
      readOnly = false,
      ...rest
    }: IFloatingLabelInputProps,
    ref: MutableRefObject<HTMLInputElement> | null
  ) => {
    const inputId = useIdCounter('floating-label-input');

    const messageId = useIdCounter('message');

    const [cursor, setCursor] = useState(0);

    const { handleBlur, handleChange, handleFocus } = useInputHandles({
      inputId,
      isMobile,
      onBlur,
      onChange,
      onFocus,
    });

    const { wrapperTestId, labelTestId, validationTestId, messageTestId } = useTestIds(testId);

    const inputProps = useMemo(
      () => ({
        'aria-describedby': messageId,
        'aria-labelledby': labelId,
        autoComplete,
        'data-testid': testId,
        hasFooter: !!hasFooter,
        id: inputId,
        maxLength,
        onBlur: handleBlur,
        onChange: (e: any) => {
          setCursor(e.target?.selectionStart);
          handleChange(e);
        },
        onFocus: handleFocus,
        pattern,
        readOnly,
        disabled: readOnly,
        type: 'text',
        validation,
        value,
      }),
      [
        messageId,
        labelId,
        autoComplete,
        testId,
        hasFooter,
        inputId,
        maxLength,
        handleBlur,
        handleFocus,
        pattern,
        readOnly,
        validation,
        value,
        handleChange,
      ]
    );

    const conditionalProps = useMemo(
      () =>
        mask
          ? {
              render: (internalRef: (n: HTMLInputElement) => void, props: typeof inputProps) => {
                // react-text-mask manages its own ref via this render method
                // in order to reference this input and allow react-text-mask
                // to references it as well, this function assigns our own ref,
                // then passes the component along to react-text-mask's ref callback
                // because the ref being passed from parent (through forwardRef)
                // is lost during this exchange, so we assign innerInput to our ref
                const refCallback = (innerInput: HTMLInputElement) => {
                  if (ref) {
                    ref.current = innerInput;
                  }
                  internalRef(innerInput);
                };
                return <StyledInput ref={refCallback} {...props} />;
              },
              mask,
            }
          : { ref },
      // eslint thinks inputProps is used here because of the type
      [mask, ref] // eslint-disable-line react-hooks/exhaustive-deps
    );

    useEffect(() => {
      // Update the selection to the previous one so that we can edit
      // the phone number without it jumping to the end of the string
      const input = ref?.current;
      if (input) {
        input.setSelectionRange(cursor, cursor);
      }
    }, [cursor, value, ref]);

    const Input: ComponentType<any> = useMemo(() => (mask ? MaskedInput : StyledInput), [mask]);
    const isInvalid = FormValidationState.INVALID === validation;

    return (
      <FloatingLabelInputWrapper className={className} data-testid={wrapperTestId}>
        <Input
          {...inputProps}
          {...conditionalProps}
          {...rest}
          data-private
          aria-invalid={isInvalid}
        />
        {hasFooter && footer}
        <Label
          id={labelId}
          htmlFor={rest.id || inputId}
          hasValue={typeof value === 'number' || !!value}
          data-testid={labelTestId}
        >
          {label}
        </Label>
        <IconContainer validation={validation} data-testid={validationTestId}>
          {validation === FormValidationState.VALID && (
            <IconCheck data-testid="input-validation-valid" />
          )}
        </IconContainer>
        {message && (
          <>
            <Message id={messageId} data-testid={messageTestId} validation={validation}>
              {message}
            </Message>
            {isInvalid && enableDefaultAlert && (
              <VisuallyHidden role="alert">{message}</VisuallyHidden>
            )}
          </>
        )}
      </FloatingLabelInputWrapper>
    );
  }
);

/** @deprecated Use FormikTextInput instead */
export const FormikFloatingLabelInput = ({
  field,
  form,
  ...props
}: FieldProps & IFloatingLabelInputProps) => {
  const { errors, touched } = form;

  const fieldTouched = touched?.[field.name];
  const error = errors?.[field.name];
  const validation = error ? FormValidationState.INVALID : FormValidationState.VALID;

  return (
    <>
      <FloatingLabelInput
        {...field}
        message={fieldTouched && error ? error : undefined}
        validation={fieldTouched && validation ? validation : undefined}
        {...props}
      />
      {error && <VisuallyHidden role="alert">{error}</VisuallyHidden>}
    </>
  );
};
